import React, { useState } from 'react';

import { link } from '@aline/routes';
import { gql } from '@apollo/client';
import { Card, Divider, Typography } from '@mui/material';
import copy from 'copy-to-clipboard';
import { Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineInput } from 'src/AlineInput';
import {
  AccountsRolesAutoComplete,
  getIsExistingUserOption,
  GroupKey,
  WhatsThis,
} from 'src/components';
import { AlineButton, AlineCheckbox, ToggleSwitch } from 'src/components/generic';
import { ClipBoardIcon } from 'src/components/icons/WorkflowLaunchConfigBackground';
import { SettingsSection } from 'src/components/workflow/workflow-detail/side-panel/SettingsSection';
import { getClientPublicOrigin } from 'src/config';
import { Apollo } from 'src/gql/generated';
import { openLink, useDispatchSuccessSnack } from 'src/helpers';
import { useGetWorkflowSimple } from 'src/hooks';
import { alineLinkButtonStyles } from 'src/theme';
import type { UserSubset } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

import { LaunchConfigToggle } from './WorkflowLaunchToggle';

const useStyles = makeStyles()(theme => ({
  addPlaceholderText: {
    ...alineLinkButtonStyles,
  },
  copyButton: {
    alignItems: 'flex-start',
    background: theme.appColors.primary,
    borderRadius: 3,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1),
  },
  copyToClipBoard: {
    alignItems: 'center',
    background: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    width: '100%',
  },
  divider: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
  flexContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'center',
    width: '100%',
  },
  inputText: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  instructionsInput: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
  },
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  ownerContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: 310,
  },
  placeholderCard: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
  },
  placeholders: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  row: {
    alignItems: 'center',
    display: 'flex',
  },
  sectionLabel: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
  },
  text: {
    marginLeft: theme.spacing(2),
  },
  workflowTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    width: '100%',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    width: '100%',
  },
}));

export function LaunchConfig({ workflowId }: { workflowId: UUID }) {
  const { classes } = useStyles();
  const { workflow } = useGetWorkflowSimple(workflowId);
  const isTWF = workflow?.variant === Apollo.WorkflowVariant.Template;

  if (!isTWF) return null;

  return (
    <SettingsSection className={classes.wrapper}>
      <div className={classes.sectionLabel}>
        <Rocket size={16} />
        <Typography className={classes.sectionTitle}>Public Launch Page</Typography>
      </div>
      <LaunchConfigToggle workflowId={workflowId} />
    </SettingsSection>
  );
}

interface WorkflowLaunchConfig extends Pick<
  Apollo.WorkflowLaunchConfig,
  'enabled' | 'id' | '__typename' | 'workflowTitle' | 'isAutomaticS4SEnabled'
> {
  launchedWorkflowOwner: UserSubset;
  placeholders: WorkflowPermissionSubset[];
}
export interface WorkflowWithLaunchConfig extends Pick<Apollo.Workflow, 'id' | '__typename'> {
  launchConfig: WorkflowLaunchConfig;
  permissions: WorkflowPermissionSubset[];
}
interface LaunchConfigBodyProps {
  workflowWithLaunchConfig: WorkflowWithLaunchConfig;
}

export function LaunchConfigBody({
  workflowWithLaunchConfig,
}: LaunchConfigBodyProps): JSX.Element | null {
  const { classes } = useStyles();
  const [workflowTitle, setWorkflowTitle] = useState<string>(
    workflowWithLaunchConfig.launchConfig.workflowTitle
  );
  const [updateWorkflowLaunchConfig] = Apollo.useUpdateWorkflowLaunchConfigMutation();
  const updateLaunchConfigTitle = useDebouncedCallback(async (launchConfigTitle: string) => {
    await updateWorkflowLaunchConfig({
      variables: {
        input: {
          launchConfigId: workflowWithLaunchConfig.launchConfig.id,
          props: {
            workflowTitle: launchConfigTitle,
          },
        },
      },
    });
  }, 1000);

  if (!workflowWithLaunchConfig.launchConfig.enabled) return null;

  return (
    <div className={classes.root}>
      <Divider className={classes.divider} />
      <div className={classes.workflowTitle}>
        <Typography className={classes.label}>Title of launched workflow:</Typography>
        <AlineInput
          fullWidth
          placeholder="Type workflow title..."
          value={workflowTitle}
          onChange={async e => {
            setWorkflowTitle(e.target.value);
            await updateLaunchConfigTitle(e.target.value);
          }}
        />
      </div>
      <LaunchConfigPlaceHolders workflowWithLaunchConfig={workflowWithLaunchConfig} />
      <div className={classes.inputText}>
        <LaunchConfigSelectOwner workflowWithLaunchConfig={workflowWithLaunchConfig} />
        <LaunchConfigAutomaticS4SToggle launchConfig={workflowWithLaunchConfig.launchConfig} />
        <div className={classes.flexContainer}>
          <Typography className={classes.label}>Copy launch link:</Typography>
          <CopyLinkToClipBoard launchConfigId={workflowWithLaunchConfig.launchConfig.id} />
        </div>
      </div>
      <AlineButton
        fullWidth
        color="primary"
        variant="contained"
        onClick={() => {
          openLink(
            link.WorkflowLaunchConfig({
              launchConfigId: workflowWithLaunchConfig.launchConfig.id,
            })
          );
        }}
      >
        Preview Launch Page
      </AlineButton>
    </div>
  );
}

export function LaunchConfigSelectOwner({ workflowWithLaunchConfig }: LaunchConfigBodyProps) {
  const { classes } = useStyles();
  const [updateWorkflowLaunchConfig] = Apollo.useUpdateWorkflowLaunchConfigMutation();

  return (
    <div className={classes.ownerContainer}>
      <Typography className={classes.label}>Select owner of the launched workflow:</Typography>
      <AccountsRolesAutoComplete
        includeOptions="users"
        suggestInviteEmail={false}
        value={{
          ...workflowWithLaunchConfig.launchConfig.launchedWorkflowOwner,
          group: GroupKey.Users,
        }}
        variant="avatar"
        onChange={async (_e, value) => {
          if (!getIsExistingUserOption(value)) return;

          await updateWorkflowLaunchConfig({
            variables: {
              input: {
                launchConfigId: workflowWithLaunchConfig.launchConfig.id,
                props: {
                  launchedWorkflowOwnerId: value.id,
                },
              },
            },
          });
        }}
      />
    </div>
  );
}

export function CopyLinkToClipBoard({ launchConfigId }: { launchConfigId: UUID }) {
  const { classes } = useStyles();
  const baseURl = getClientPublicOrigin();
  const path = link.WorkflowLaunchConfig({ launchConfigId });
  const dispatchLinkCopied = useDispatchSuccessSnack({
    message: 'Link copied to clipboard',
  });
  const linkURL = baseURl + path;

  return (
    <div className={classes.copyToClipBoard}>
      <Typography noWrap>{linkURL}</Typography>
      <div
        className={classes.copyButton}
        onClick={() => {
          copy(linkURL);
          dispatchLinkCopied();
        }}
      >
        <ClipBoardIcon />
      </div>
    </div>
  );
}

function LaunchConfigPlaceHolders({ workflowWithLaunchConfig }: LaunchConfigBodyProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [updateWorkflowLaunchConfig] = Apollo.useUpdateWorkflowLaunchConfigMutation();
  const selectedPlaceholderIdSet = new Set(
    workflowWithLaunchConfig.launchConfig.placeholders.map(p => p.id)
  );
  const launchCollaborators = workflowWithLaunchConfig.permissions.filter(p => {
    const hasUnassignedRole = Boolean(p.roleName) && !p.account;
    const isQueued = p.timeliness === Apollo.WorkflowPermissionTimeliness.Queued;
    // Launch fills the launcher with the configured owner, so the submitter never picks it.
    return hasUnassignedRole && isQueued && !p.isLauncher;
  });
  const placeholdersCopy =
    'Select placeholders that your counterparty should assign when launching the workflow:';

  if (!launchCollaborators.length)
    return (
      <>
        <Typography className={classes.label}>{placeholdersCopy}</Typography>
        <div>
          <Typography
            sx={{
              display: 'inline',
            }}
          >
            Please{' '}
          </Typography>
          <Typography
            className={classes.addPlaceholderText}
            sx={{
              display: 'inline',
            }}
            onClick={() =>
              navigate(
                link.WorkflowDetailTabCollaborators({ workflowId: workflowWithLaunchConfig.id })
              )
            }
          >
            add placeholders
          </Typography>
          <Typography
            sx={{
              display: 'inline',
            }}
          >
            {' '}
            for your counterparty to assign on launch.
          </Typography>
        </div>
      </>
    );

  return (
    <>
      <Typography className={classes.label}>{placeholdersCopy}</Typography>
      <div className={classes.placeholders}>
        {launchCollaborators.map(permission => {
          return (
            <Card key={permission.id} className={classes.placeholderCard}>
              <Typography noWrap>{permission.roleName || 'Untitled'}</Typography>
              <AlineCheckbox
                checked={selectedPlaceholderIdSet.has(permission.id)}
                onChange={async () => {
                  const isChecked = selectedPlaceholderIdSet.has(permission.id);
                  const newPlaceholderIds = isChecked
                    ? selectedPlaceholderIdSet
                        .values()
                        .filter(id => id !== permission.id)
                        .toArray()
                    : [...selectedPlaceholderIdSet, permission.id];
                  await updateWorkflowLaunchConfig({
                    variables: {
                      input: {
                        launchConfigId: workflowWithLaunchConfig.launchConfig.id,
                        placeholderIds: newPlaceholderIds,
                      },
                    },
                  });
                }}
              />
            </Card>
          );
        })}
      </div>
    </>
  );
}

const useLaunchConfigAutomaticS4SToggleStyles = makeStyles()(theme => ({
  whatsThisWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

export function LaunchConfigAutomaticS4SToggle({
  launchConfig,
}: {
  launchConfig: Pick<Apollo.WorkflowLaunchConfig, 'id' | '__typename' | 'isAutomaticS4SEnabled'>;
}) {
  const { classes } = useLaunchConfigAutomaticS4SToggleStyles();

  return (
    <div className={classes.wrapper}>
      <div className={classes.whatsThisWrapper}>
        <Typography variant="subtitle2">Automatic send for signature</Typography>
        <WhatsThis tooltipMsg="Turning this on automatically sends your workflow for signature vs. having to send for signature manually" />
      </div>
      <AutomaticS4SToggle launchConfig={launchConfig} />
    </div>
  );
}

void gql`
  mutation UpdateWorkflowLaunchAutomaticS4SConfig($input: UpdateWorkflowLaunchConfigInput!) {
    updateWorkflowLaunchConfig(input: $input) {
      __typename
      id
      isAutomaticS4SEnabled
    }
  }
`;

const useS4SStyles = makeStyles()(theme => ({
  wrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    height: '100%',
  },
}));

export function AutomaticS4SToggle({
  launchConfig,
}: {
  launchConfig: Pick<Apollo.WorkflowLaunchConfig, 'id' | '__typename' | 'isAutomaticS4SEnabled'>;
}): JSX.Element {
  const { classes } = useS4SStyles();
  const [updateWorkflowLaunchAutomaticS4SConfig] =
    Apollo.useUpdateWorkflowLaunchAutomaticS4SConfigMutation();

  async function handleToggle(isAutomaticS4SEnabled: boolean) {
    await updateWorkflowLaunchAutomaticS4SConfig({
      optimisticResponse: {
        __typename: 'Mutation',
        updateWorkflowLaunchConfig: {
          __typename: 'WorkflowLaunchConfig',
          id: launchConfig.id,
          isAutomaticS4SEnabled: isAutomaticS4SEnabled,
        },
      },
      variables: {
        input: {
          launchConfigId: launchConfig.id,
          props: { isAutomaticS4SEnabled },
        },
      },
    });
  }

  return (
    <div className={classes.wrapper}>
      <ToggleSwitch
        toggle={async () => {
          await handleToggle(!launchConfig.isAutomaticS4SEnabled);
        }}
        toggled={launchConfig.isAutomaticS4SEnabled}
      />
      <Typography>{launchConfig.isAutomaticS4SEnabled ? 'Enabled' : 'Disabled'}</Typography>
    </div>
  );
}
